///
/// EarthBrowser
///
/// Created by Matt Giger on 12/4/2025
/// Copyright (c) 2025 Geodata Labs
///

import type { Camera } from "@stovokor/camera/Camera"
import { CameraState } from "@stovokor/camera/CameraState"
import { animation, easing, type easingfunc } from "@stovokor/math/animation"
import { quaternion } from "@stovokor/math/quaternion"
import { vec3, vec3lerp, normal } from "@stovokor/math/vector"
import { lerp } from "@stovokor/math/math"

/**
 * Extended camera transition parameters
 */
export interface FocusTransitionParams {
	/** Duration of the transition in seconds (default: 1.0) */
	duration?: number
	/** Easing function name or custom function (default: "easeInOut") */
	easing?: "linear" | "easeIn" | "easeOut" | "easeInOut" | easingfunc
	/** Optional callback during transition with progress 0-1 */
	onProgress?: (progress: number) => void
	/** Optional callback when transition completes */
	onComplete?: () => void
	/**
	 * When true, immediately adopt the current camera state with no animation.
	 * This is useful when the user starts interacting (e.g., pan) and you want
	 * to switch focus without moving the camera from its current position.
	 * When set, duration is ignored and treated as 0.
	 */
	preserveCurrentState?: boolean
}

/**
 * Target state for a focus transition
 * Can be a static state or a function that returns dynamic state (for tracking moving targets)
 */
export type TransitionTarget = CameraState | (() => CameraState)

/**
 * FocusTransition - Manages animated camera transitions between focus states
 *
 * This class handles smooth camera motion and rotation when focus changes
 * between nodes (e.g., from Earth to a satellite, or between satellites).
 *
 * Usage:
 * ```typescript
 * const transition = new FocusTransition(camera)
 *
 * // Transition to a static target
 * transition.start(targetState, { duration: 1.5 })
 *
 * // Transition to a moving target (e.g., satellite)
 * transition.start(() => satellite.getCameraState(), { duration: 2.0 })
 * ```
 */
export class FocusTransition {
	private camera: Camera
	private anim: animation
	private isRunning = false
	private startState?: CameraState
	private targetProvider?: TransitionTarget
	private progressCallback?: (progress: number) => void
	private completeCallback?: () => void

	constructor(camera: Camera) {
		this.camera = camera
		this.anim = new animation()
			.from(0)
			.to(1)
	}

	/**
	 * Whether a transition is currently running
	 */
	public get running(): boolean {
		return this.isRunning
	}

	/**
	 * Start a transition to a new camera state
	 *
	 * @param target Static CameraState or function returning dynamic state
	 * @param params Transition parameters (duration, easing, callbacks)
	 */
	public start(target: TransitionTarget, params: FocusTransitionParams = {}): void {
		// Stop any existing transition
		this.stop()

		const duration = params.duration ?? 1.0

		// Handle instant transitions (duration 0) - skip animation entirely
		if (duration <= 0) {
			this.targetProvider = target
			// Don't snap to target - keep camera where it is
			// Just call the completion callback
			params.onProgress?.(1.0)
			params.onComplete?.()
			return
		}

		const easingFn = this.resolveEasing(params.easing ?? "easeInOut")

		// Capture start state
		this.startState = new CameraState(
			this.camera.position,
			this.camera.state.forward,
			this.camera.state.up
		)
		this.targetProvider = target
		this.progressCallback = params.onProgress
		this.completeCallback = params.onComplete
		this.isRunning = true

		this.anim
			.duration(duration)
			.easing(easingFn)
			.onUpdate((progress: number) => {
				this.updateTransition(progress)
				this.progressCallback?.(progress)
			})
			.onComplete(() => {
				this.finishTransition()
			})
			.start()
	}

	/**
	 * Stop the current transition immediately
	 */
	public stop(): void {
		if (this.isRunning) {
			this.anim.stop()
			this.isRunning = false
			this.startState = undefined
			this.targetProvider = undefined
			this.progressCallback = undefined
			this.completeCallback = undefined
		}
	}

	/**
	 * Cancel the transition and optionally snap to target
	 */
	public cancel(snapToTarget = false): void {
		if (this.isRunning && snapToTarget && this.targetProvider) {
			const target = this.getTargetState()
			this.camera.state.position = target.position
			this.camera.state.forward = target.forward
			this.camera.state.up = target.up
		}
		this.stop()
	}

	private updateTransition(progress: number): void {
		if (!this.startState || !this.targetProvider) return

		const target = this.getTargetState()
		const state = this.camera.state

		// Calculate quaternion rotations from start to target
		// This is the same algorithm used in the old CameraState.animateTo()
		const fwdRot = quaternion.fromVec3ToVec3(this.startState.forward, target.forward)
		const upRot = quaternion.fromVec3ToVec3(this.startState.up, target.up)

		// Animate forward using quaternion rotation with pow for smooth interpolation
		const fquat = fwdRot.pow(progress)
		state.forward = fquat.rotateVector(this.startState.forward)

		// Animate up using quaternion rotation with pow for smooth interpolation
		const uquat = upRot.pow(progress)
		state.up = uquat.rotateVector(this.startState.up)

		// Interpolate position: lerp the direction and radius separately
		// This matches the old algorithm for smooth spherical motion
		const startLen = this.startState.position.length
		const targetLen = target.position.length
		const radius = lerp(startLen, targetLen, progress)

		// Linear interpolate the position vector, then normalize and scale by interpolated radius
		const pos = vec3lerp(this.startState.position, target.position, progress)
		state.position = normal(pos).multiply(radius)
	}

	private finishTransition(): void {
		if (!this.targetProvider) return

		// Snap to final target state
		const target = this.getTargetState()
		this.camera.state.position = target.position
		this.camera.state.forward = target.forward
		this.camera.state.up = target.up

		const callback = this.completeCallback
		this.isRunning = false
		this.startState = undefined
		this.targetProvider = undefined
		this.progressCallback = undefined
		this.completeCallback = undefined

		callback?.()
	}

	private getTargetState(): CameraState {
		if (!this.targetProvider) {
			return new CameraState()
		}
		return typeof this.targetProvider === "function"
			? this.targetProvider()
			: this.targetProvider
	}

	private resolveEasing(e: "linear" | "easeIn" | "easeOut" | "easeInOut" | easingfunc): easingfunc {
		if (typeof e === "function") return e
		switch (e) {
			case "linear": return easing.linear
			case "easeIn": return easing.quadraticIn
			case "easeOut": return easing.quadraticOut
			case "easeInOut": return easing.quadraticInOut
			default: return easing.quadraticInOut
		}
	}
}